import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./HeaderProfile.module.scss";
import { Popup } from "@progress/kendo-react-popup";
import BellImage from "../../assets/bell-not.svg";
import { SvgIcon } from "@progress/kendo-react-common";
import { Badge, Loader } from "@progress/kendo-react-indicators";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import { getNotifications, updateNotifications } from "api";
import { shortMonths } from "components/MyMatters";
import { IconBackground } from "components/common/DocumentViewer/DocumentViewerComponents";
import { changeLedgerLink } from "actions/ledgerLinks.action";
import { LuBellRing } from "react-icons/lu";
import { RxCross2 } from "react-icons/rx";

const formatNotificationTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const isSameCalendarDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const formatNotificationDate = (dateUTC) => {
  if (!dateUTC) return "";
  const date = new Date(dateUTC);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const time = formatNotificationTime(date);

  if (isSameCalendarDay(date, now)) return `Today ${time}`;
  if (isSameCalendarDay(date, yesterday)) return `Yesterday ${time}`;

  return `${date.getDate()} ${shortMonths[date.getMonth()]} ${time}`;
};

const LINK_HANDLERS = {
  OpenCaseFileLedger: (item, { navigate, onNavigate }) => {
    const matterId = item.matterId ?? item.matterID;
    if (!matterId) return;
    navigate(`/matters/${matterId}?tab=ledger`);
    onNavigate?.();
  },
  OpenMatterLedger: (item, { navigate, dispatch, onNavigate }) => {
    const matterId = item.matterId ?? item.matterID;
    const matterRef = item.matterRef;
    if (!matterId || !matterRef) return;
    dispatch(changeLedgerLink("MatterLedger", { matterId, matterRef }));
    navigate("/accounts/matterledger");
    onNavigate?.();
  },
};

const handleNotificationLink = (item, deps) => {
  const handler = item.linkFunction && LINK_HANDLERS[item.linkFunction];
  if (handler) handler(item, deps);
};

const Notifications = () => {
  const notificationRef = React.useRef();
  const [notifications, setNotifications] = useState([]);
  const anchor = React.useRef();
  const [show, setShow] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    getApiNotifications();
  }, []);

  const getApiNotifications = async (showLoading = false) => {
    try {
      if (showLoading) setIsRefreshing(true);
      const notifications = await getNotifications();
      setNotifications(notifications?.data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      if (showLoading) setIsRefreshing(false);
    }
  };

  const handleRefresh = (e) => {
    e.stopPropagation();
    if (isRefreshing) return;
    getApiNotifications(true);
  };

  const updateNotificationData = async (item, seen) => {
    try {
      await updateNotifications([item.id], seen);
      await getApiNotifications();
    } catch (error) {}
  };

  const getNotificationsCount = () => {
    const count = notifications.filter((item) => item.seen === false);
    if (count.length >= 10) return "9+";
    return count.length;
  };

  const linkDeps = {
    navigate,
    dispatch,
    onNavigate: () => setShow(false),
  };

  return (
    <>
      <div
        className={`position-relative d-flex align-items-center justify-content-center ${!show ? "cursor-pointer" : ""}`}
        ref={anchor}
        style={{ width: "40px", height: "40px", zIndex: 20 }}
        onClick={!show ? () => setShow(true) : undefined}
      >
        {!show && (
          <div className={`${styles["notifications-border"]} p-2`}>
            <img src={BellImage} alt="" />
            {!!getNotificationsCount() && (
              <Badge className={`${styles["notification-badge"]} ms-2`}>
                <span>{getNotificationsCount()}</span>
              </Badge>
            )}
          </div>
        )}
      </div>

      {show && (
        <div
          className={styles["notification-backdrop"]}
          onClick={() => setShow(false)}
          aria-hidden="true"
        />
      )}
      <div className={`${styles["notification-popup"]}`} ref={notificationRef}>
        <Popup
          show={show}
          appendTo={null}
          popupClass={styles["notification-content"]}
          animate={false}
          anchor={anchor.current}
          positionMode="fixed"
          style={{ zIndex: 110 }}
        >
          <div className={styles["notification-panel"]}>
            <div className="w-100">
              <div
                className={`p-4 d-flex align-items-center justify-content-between w-100`}
              >
                <p className="mb-0 fw-semibold">Notifications</p>
                <div className={`d-flex align-items-center gap-2`}>
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className="common-btn d-flex align-items-center justify-content-center"
                    title="Refresh"
                    disabled={isRefreshing}
                    style={{
                      width: "26px",
                      height: "26px",
                      minWidth: "26px",
                      padding: 0,
                      lineHeight: 0,
                    }}
                  >
                    {isRefreshing ? (
                      <Loader
                        size="small"
                        type="infinite-spinner"
                        themeColor="light"
                      />
                    ) : (
                      <SvgIcon
                        icon={refreshIcon}
                        style={{ width: 16, height: 16, display: "block" }}
                      />
                    )}
                  </button>
                  <IconBackground onClick={() => setShow(false)} title="Close">
                    <RxCross2
                      size={17}
                      color="red"
                      strokeWidth={1}
                      role="button"
                    />
                  </IconBackground>
                </div>
              </div>
              {/* </div> */}
              <div className="w-100 border-bottom border-opacity-50"></div>
            </div>
            <div
              className={`container-fluid ${styles["not-container"]}`}
              role="button"
            >
              {notifications.map((item, index) => (
                <NotificationsItem
                  item={item}
                  key={item.id}
                  updateNotification={updateNotificationData}
                  onLinkClick={() => handleNotificationLink(item, linkDeps)}
                />
              ))}
            </div>
          </div>
        </Popup>
      </div>
    </>
  );
};

export default Notifications;

const NotificationsItem = ({ item, updateNotification, onLinkClick }) => {
  const { seen, details, dateUTC, matterRef, linkFunction } = item;
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  const isLinkableMatter =
    matterRef && linkFunction && LINK_HANDLERS[linkFunction];

  const handleMatterClick = (e) => {
    e.stopPropagation();
    if (isLinkableMatter) onLinkClick();
  };

  const handleToggleSeen = async (e) => {
    e.stopPropagation();
    if (isMarkingRead) return;
    setIsMarkingRead(true);
    try {
      await updateNotification(item, seen ? false : undefined);
    } finally {
      setIsMarkingRead(false);
    }
  };

  return (
    <div
      className={`d-flex gap-2 ps-2 py-2 align-items-center  justify-content-between ${styles["notification-item"]}`}
    >
      <div style={{ flex: 1 }}>
        <p
          className={`${styles["notification-details"]} mb-1 fs-sm ${
            !seen ? "fw-bold" : "fw-normal"
          }`}
        >
          {details}
        </p>
        {matterRef && (
          <p className={`${styles["notification-matter"]} mb-1`}>
            Matter:{" "}
            {isLinkableMatter ? (
              <button
                type="button"
                className={styles["notification-matter-link"]}
                onClick={handleMatterClick}
              >
                {matterRef}
              </button>
            ) : (
              <span>{matterRef}</span>
            )}
          </p>
        )}
        <p className={`${styles["notification-date"]} mb-0`}>
          {formatNotificationDate(dateUTC)}
        </p>
      </div>
      <button
        type="button"
        className={`${styles["notification-action"]} ${seen ? styles["notification-action-read"] : ""}`}
        onClick={handleToggleSeen}
        disabled={isMarkingRead}
        aria-label={
          isMarkingRead
            ? "Processing"
            : seen
              ? "Mark as unread"
              : "Mark as read"
        }
      >
        {isMarkingRead ? (
          <Loader size="small" type="infinite-spinner" />
        ) : (
          <>
            <LuBellRing size={18} />
            <span>{seen ? "Mark as unread" : "Mark as read"}</span>
          </>
        )}
      </button>
    </div>
  );
};
